import React, { useEffect, createContext, useState } from 'react'
import { AuthProvider, useAuth, UserManager } from 'oidc-react'

import { getRoles } from '../../utils/user/permissions'
import withConfig from '../wrappers/withConfig'

const UserContext = createContext(null)
const INSTANCE_ID_KEY = 'app_instance_id'

const getInstanceId = () => {
    // Check sessionStorage for existing instance ID
    const storedId = sessionStorage.getItem(INSTANCE_ID_KEY)
    if (storedId) {
        return storedId
    }

    // Generate new instance ID if none exists
    const id =
        window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
                  /[xy]/g,
                  function (c) {
                      const r = (Math.random() * 16) | 0,
                          v = c === 'x' ? r : (r & 0x3) | 0x8
                      return v.toString(16)
                  }
              )

    // Store in sessionStorage
    sessionStorage.setItem(INSTANCE_ID_KEY, id)
    return id
}

const AppUserContextProvider = withConfig(
    ({ config, loadingUser, children }) => {
        const { SHOULD_LOGOUT_ON_SIMULTANEOUS_SESSION, API_URL } = config
        const { userManager: client } = useAuth()
        const [roles, setRoles] = useState('')
        const [user, setUser] = useState(null)
        const [instanceId, setInstanceId] = useState(null)

        useEffect(() => {
            const id = getInstanceId()
            setInstanceId(id)
        }, [])

        useEffect(() => {
            if (!user || !instanceId) return

            // Create AbortController for cancellation
            let abortController = new AbortController()

            const checkIfInstanceIsAllowed = async (signal) => {
                try {
                    const response = await fetch(
                        `${config.API_URL}/Authentication/checkInstanceIDs?instanceId=${instanceId}&sub=${user.profile.sub}`,
                        { signal } // Pass the signal to fetch
                    )

                    const res = await response.json()

                    if (!res.allowed) {
                        await client.signoutRedirect()
                        setInstanceId(null)
                        sessionStorage.removeItem(INSTANCE_ID_KEY)
                    }
                } catch (error) {
                    // Ignore abort errors
                    if (error.name === 'AbortError') {
                        console.log('Instance check was cancelled')
                        return
                    }
                    console.error('Failed to check instance access:', error)
                }
            }

            // Run immediately on mount/user change
            checkIfInstanceIsAllowed(abortController.signal)

            // Set up interval to run every 10 seconds
            const intervalId = setInterval(() => {
                // Create new AbortController for each interval request
                const intervalAbortController = new AbortController()
                if (SHOULD_LOGOUT_ON_SIMULTANEOUS_SESSION) {
                    checkIfInstanceIsAllowed(intervalAbortController.signal)
                }
            }, 10000)

            return () => {
                // Abort any ongoing request when component unmounts or dependencies change
                abortController.abort()
                clearInterval(intervalId)
            }
        }, [user, instanceId, , client])

        // when the user changes, update roles list
        useEffect(() => {
            setRoles(getRoles(user))
        }, [user])

        useEffect(() => {
            if (!instanceId) {
                return
            }

            const getUser = async () => {
                try {
                    const u = await client.getUser()
                    setUser(u)
                } catch (e) {
                    console.log('UserContext: Error getting user -- ', e)
                }
            }

            if (client) {
                getUser()
            }
        }, [client, instanceId, config.API_URL])

        return (
            <UserContext.Provider
                value={{
                    user,
                    roles,
                    client,
                    loadingUser,
                    instanceId,
                }}
            >
                {children}
            </UserContext.Provider>
        )
    }
)

const UserContextProvider = withConfig(({ config, children }) => {
    const settings = {
        authority: config.REACT_APP_OIDC_AUTHORITY,
        client_id: config.REACT_APP_OIDC_CLIENT_ID,
        client_secret: config.REACT_APP_OIDC_CLIENT_SECRET,
        redirect_uri: config.REACT_APP_OIDC_CLIENT_REDIRECT_URI,
        silent_redirect_uri: config.REACT_APP_OIDC_CLIENT_SILENT_REDIRECT_URI,
        response_type: config.REACT_APP_OIDC_RESPONSE_TYPE,
        scope: config.REACT_APP_OIDC_SCOPE,
        post_logout_redirect_uri:
            config.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI,
        automaticSilentRenew: true,
    }

    const userManager = new UserManager(settings)
    return (
        <AuthProvider userManager={userManager} autoSignIn={false}>
            <AppUserContextProvider>{children}</AppUserContextProvider>
        </AuthProvider>
    )
})

export { UserContext }
export default UserContextProvider
